import React, { useState } from 'react';
import { TimePeriod, PricingItem } from 'common/types';
import { Button, Box } from '@mui/material';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { getTimePeriodsMap } from 'views/Product/__utils/time';
import TimeValueSelect from 'views/Product/__components/TimeValueSelect';
import TimePeriodSelect from 'views/Product/__components/TimePeriodSelect';

import { useTranslation } from 'services/localization/useTranslation';

const TIME_PERIODS = getTimePeriodsMap();

interface Props {
	onAdd: (value: PricingItem) => void;
	disabled?: boolean;
	defaultValues?: {
		timeValue: number;
		timePeriod: TimePeriod;
		price?: number;
		multiplier?: number;
		channels?: PricingItem['channels'];
	};
}

const PricingTableRowAdd = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const [timeValue, setTimeValue] = useState(props.defaultValues?.timeValue ?? 1);
	const [timePeriod, setTimePeriod] = useState(
		props.defaultValues?.timePeriod ?? TIME_PERIODS.HOURS,
	);

	const handleTimePeriodChange = (value: TimePeriod) => {
		setTimePeriod(value);
		setTimeValue(1);
	};

	const handleAdd = () => {
		props.onAdd({
			timeValue,
			timePeriod,
			channels: props.defaultValues?.channels ?? ['ALL'],
			multiplier: props.defaultValues?.multiplier ?? 0,
			price: props.defaultValues?.price ?? 0,
		});

		if (timeValue < 52) {
			setTimeValue(timeValue + 1);
		}
	};

	return (
		<Box className={classes.wrapper}>
			<Box display="flex" flexDirection="row">
				<TimeValueSelect
					value={timeValue}
					onChange={setTimeValue}
					textFieldProps={{
						variant: 'outlined',
						label: '',
						fullWidth: false,
						margin: 'none',
						disabled: props.disabled,
					}}
				/>
				<Box flex="1" ml={1}>
					<TimePeriodSelect
						value={timePeriod}
						onChange={(t) => handleTimePeriodChange(t as TimePeriod)}
						labelFormat={timeValue === 1 ? 'singular' : 'plural'}
						disableNone
						textFieldProps={{
							label: '',
							margin: 'none',
							variant: 'outlined',
							fullWidth: true,
							disabled: props.disabled,
						}}
					/>
				</Box>
			</Box>
			<Box mt={1} mr={1} />
			<Box>
				<Button fullWidth color="primary" variant="outlined" onClick={handleAdd}>
					+ {t('common:actions.addRow', 'Add row')}
				</Button>
			</Box>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			width: '100%',
			display: 'flex',
			flexDirection: 'row',
			flexWrap: 'wrap',
			alignItems: 'center',
			[theme.breakpoints.down('sm')]: {
				flexDirection: 'column',
				alignItems: 'stretch',
			},
		},
	}),
);

export default PricingTableRowAdd;
